import React, { useEffect, useState } from 'react'
import { FlatList, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useStores } from '@trivie/core'
import {
  color,
  Header,
  iconArrowBack,
  iconMoreHorizontal,
  iconPeople,
  SvgIcon,
  TabController,
  Text,
} from '@trivie/ui-mobile'
import { AlltimeLeaderboardTab } from './tabs/all-time-leaderboard-tab'
import { DailyLeaderboardTab } from './tabs/daily-leaderboard-tab'
import { MonthlyLeaderboardTab } from './tabs/monthly-leaderboard-tab.tsx'
import { WeeklyLeaderboardTab } from './tabs/weekly-leaderboard-tab.tsx'
import { useHistory } from 'react-router-dom'

export const LeaderboardScreen = observer((props) => {
  const { leaderboardStore, userStore } = useStores()

  const { user, enterpriseTier } = userStore
  const { tags, getLeaderboardRank, getLeaderboards, getLeaderboardByTag } = leaderboardStore

  const gdpr_enabled = user?.workspace_settings?.gdpr_enabled

  const userLeaderboardsDisabled =
    (!user?.workspace_settings?.user_leaderboard_alltime_enabled &&
      !user?.workspace_settings?.user_leaderboard_monthly_enabled &&
      !user?.workspace_settings?.user_leaderboard_weekly_enabled &&
      !user?.workspace_settings?.user_leaderboard_daily_enabled) ||
    gdpr_enabled

  const [leaderboardsDisabled, setLeaderboardsDisabled] = useState(userLeaderboardsDisabled)

  const [showLeaderboardMenu, setShowLeaderboardMenu] = useState(false)
  const toggleLeaderboardMenu = () => setShowLeaderboardMenu(!showLeaderboardMenu)
  const [selectedTag, setSelectedTag] = useState('')

  const handleDefaultTime = () => {
    if (user?.workspace_settings?.user_leaderboard_alltime_enabled) {
      return 'all_time'
    } else if (user?.workspace_settings?.user_leaderboard_monthly_enabled) {
      return 'monthly'
    } else if (user?.workspace_settings?.user_leaderboard_weekly_enabled) {
      return 'weekly'
    } else if (user?.workspace_settings?.user_leaderboard_daily_enabled) {
      return 'daily'
    }
  }

  const [filter, setFilter] = useState(
    leaderboardsDisabled ? 'all_time' : handleDefaultTime() || '',
  )

  const load = (leaderboardFilter, selected?) => {
    if (!enterpriseTier) {
      getLeaderboards(leaderboardFilter)
    } else {
      if (selected) {
        getLeaderboardByTag(selected, leaderboardFilter)
      } else if ((selectedTag === 'User' || selectedTag === '') && !userLeaderboardsDisabled) {
        getLeaderboards(leaderboardFilter)
      } else {
        getLeaderboardByTag(selectedTag, leaderboardFilter)
      }
    }
  }

  useEffect(() => {
    if (user) {
      userStore.getUser(user.id)
      if (enterpriseTier) {
        leaderboardStore.getTagLeaderboards(user.id).then((data: any) => {
          if (data.length > 0 && userLeaderboardsDisabled) {
            setLeaderboardsDisabled(false)
            setSelectedTag(data[0])
            getLeaderboardByTag(data[0], filter)
          }
        })
      }
      if (!userLeaderboardsDisabled) {
        load(getActiveFilter())
      }
    }
  }, [])

  const handleGroupChange = (item: string, index: number) => {
    setSelectedTag(item)
    setFilter(getActiveFilter())
    if (index === 0 && !userLeaderboardsDisabled) {
      setSelectedTag('User')
    }
    load(getActiveFilter(), item)
    setShowLeaderboardMenu(false)
  }

  const [activeTab, setActiveTab] = useState(3)

  const getActiveFilter = () => {
    switch (activeTab) {
      case 0:
        return 'daily'
      case 1:
        return 'weekly'
      case 2:
        return 'monthly'
      case 3:
        return 'all_time'
      default:
        return 'all_time'
    }
  }

  const tabs = [
    {
      name: 'daily',
      enable:
        user?.workspace_settings?.user_leaderboard_daily_enabled ||
        (selectedTag &&
          selectedTag !== 'User' &&
          user?.workspace_settings?.group_leaderboard_daily_enabled &&
          !leaderboardsDisabled),
      label: 'Leaderboard.today',
      component: (
        <DailyLeaderboardTab
          load={load}
          userLeaderboardDisabled={userLeaderboardsDisabled}
          selectedTag={selectedTag}
        />
      ),
      onPress: () => load('daily'),
    },
    {
      name: 'weekly',
      enable:
        user?.workspace_settings?.user_leaderboard_weekly_enabled ||
        (selectedTag &&
          selectedTag !== 'User' &&
          user?.workspace_settings?.group_leaderboard_weekly_enabled &&
          !leaderboardsDisabled),
      label: 'Leaderboard.week',
      component: (
        <WeeklyLeaderboardTab
          load={load}
          userLeaderboardDisabled={userLeaderboardsDisabled}
          selectedTag={selectedTag}
        />
      ),
      onPress: () => load('weekly'),
    },
    {
      name: 'monthly',
      enable:
        user?.workspace_settings?.user_leaderboard_monthly_enabled ||
        (selectedTag &&
          selectedTag !== 'User' &&
          user?.workspace_settings?.group_leaderboard_monthly_enabled &&
          !leaderboardsDisabled),
      label: 'Leaderboard.month',
      component: (
        <MonthlyLeaderboardTab
          load={load}
          userLeaderboardDisabled={userLeaderboardsDisabled}
          selectedTag={selectedTag}
        />
      ),
      onPress: () => load('monthly'),
    },
    {
      name: 'all_time',
      enable:
        user?.workspace_settings?.user_leaderboard_alltime_enabled ||
        (selectedTag &&
          selectedTag !== 'User' &&
          user?.workspace_settings?.group_leaderboard_alltime_enabled &&
          !leaderboardsDisabled),
      label: 'Leaderboard.allTime',
      component: (
        <AlltimeLeaderboardTab
          load={load}
          userLeaderboardDisabled={userLeaderboardsDisabled}
          selectedTag={selectedTag}
        />
      ),
      onPress: () => load('all_time'),
    },
  ]

  const { t } = useTranslation()

  const history = useHistory()

  return (
    <View style={FULL}>
      <View style={{ ...CONTAINER, height: '100%' }}>
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <div style={HEADER_ITEM1}>
              <Header
                leftIcon={iconArrowBack}
                onLeftPress={history.goBack}
                rightIcon={enterpriseTier && tags?.length >= 1 ? iconMoreHorizontal : false}
                rightDataSet={{ qat: 'leaderboard_menu_btn' }}
                onRightPress={tags?.length >= 1 ? toggleLeaderboardMenu : () => {}}
                center
                headerText={`${t('Leaderboard.tagHeader', { tag: selectedTag ?? 'User' })} `}
              />
            </div>
          </div>
        </div>
        <TabController setActiveTab={setActiveTab} activeTab={activeTab} tabs={tabs} />
        {showLeaderboardMenu && (
          <LeaderboardMenu
            gdpr_enabled={gdpr_enabled}
            leaderboardsDisabled={userLeaderboardsDisabled}
            tags={tags}
            onPressItem={handleGroupChange}
          />
        )}
      </View>
    </View>
  )
})

interface LeaderboardMenuProps {
  onPressItem: (item: string, index: number) => void
  tags: string[]
  leaderboardsDisabled?: boolean
  gdpr_enabled: boolean | undefined
}
const LeaderboardMenu = observer((props: LeaderboardMenuProps) => {
  const { gdpr_enabled, onPressItem, leaderboardsDisabled } = props

  const { leaderboardStore } = useStores()
  const { tags } = leaderboardStore

  const data = leaderboardsDisabled || gdpr_enabled ? tags : ['', ...tags]

  const { t } = useTranslation()

  const renderMenuItem = ({ item, index }: any) => {
    return (
      <TouchableOpacity
        style={{ ...MENU_ITEM, borderBottomWidth: index !== data.length - 1 ? 1 : 0 }}
        onPress={() => onPressItem(item, index)}
      >
        <Text
          tx={index === 0 && !leaderboardsDisabled ? t('Leaderboard.userLeaderboard') : item}
          variant="label"
        />
        <SvgIcon src={iconPeople} color="#000" />
      </TouchableOpacity>
    )
  }
  return (
    <FlatList
      style={LEADERBOARD_MENU}
      showsVerticalScrollIndicator={false}
      data={data}
      renderItem={renderMenuItem}
    />
  )
})

/**
 * Styling
 */
const FULL: ViewStyle = { flex: 1, height: '100%' }
const CONTAINER: ViewStyle = { backgroundColor: color.white }
const LEADERBOARD_MENU: ViewStyle = {
  width: 254,
  position: 'absolute',
  right: 20,
  top: 100,
  maxHeight: 352,
  overflow: 'scroll',
  backgroundColor: 'rgb(237, 237, 237)',
  zIndex: 999,
  borderRadius: 12,
}
const MENU_ITEM: ViewStyle = {
  paddingVertical: 11,
  paddingHorizontal: 16,
  height: 44,
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  borderBottomColor: 'rgba(60, 60, 67, 0.36)',
}

const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}

const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER_ITEM1: React.CSSProperties = {
  width: '100%',
}
